<template>
  <div class="game-settings-container">
    <h1 class="game-title">{{ $t('PvPgame.PvPgameTitle') }}</h1>
    <div class="top-action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('PvPgame.clearData') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('PvPgame.saveSettings') }}</button>
    </div>
    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.topic') }}</div>
      <div class="vocabulary-counter">
        {{ topic.length }}/20
      </div>
      <input class="input-text" type="text" v-model="topic" :placeholder="$t('PvPgame.inputTopic')" maxlength="20">
    </div>
  
    <div class="setting-section correct-section">
      <div class="setting-title">{{ $t('PvPgame.correctItems') }}</div>
      <div class="vocabulary-counter">
        {{ vocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="vocabularyInput" :placeholder="$t('PvPgame.correctItemsPlaceholder')"
          @keydown="handleKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags">
        <div v-for="(word, index) in vocabularyList" :key="index" class="vocabulary-tag">
          {{ word }}
          <span class="delete-tag" @click="removeVocabulary(index)">×</span>
        </div>
      </div>


    </div>


    <div class="setting-section error-section">
      <div class="setting-title">{{ $t('PvPgame.errorItems') }}</div>
      <div class="vocabulary-counter">
        {{ errorVocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="errorVocabularyInput" :placeholder="$t('PvPgame.errorItemsPlaceholder')"
          @keydown="handleErrorKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags error-tags">
        <div v-for="(word, index) in errorVocabularyList" :key="index" class="vocabulary-tag error-tag">
          {{ word }}
          <span class="delete-tag" @click="removeErrorVocabulary(index)">×</span>
        </div>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.gameDuration') }}</div>
      <div class="duration-selector">
        <select v-model="gameDuration">
          <option v-for="option in durationOptions" :key="option" :value="option">
            {{ option }} {{ $t('PvPgame.second') }}
          </option>
        </select>
      </div>
    </div>
    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.gameSpeed') }}</div>
      <div class="duration-selector">
        <select v-model="gameSpeed">
          <option v-for="option in speedOptions" :key="option" :value="option">
            {{ getSpeedText(option) }}
          </option>
        </select>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.backgroundSetting') }}</div>
      <div class="background-selector">
        <div
          v-for="option in backgroundData"
          :key="option.value"
          class="background-option"
          :class="{ 'selected': background === option.value }"
          @click="background = option.value"
        >
          <img :src="option.image" />
          <div class="background-label">{{ $t('PvPgame.'+option.value) }}</div>
        </div>
      </div>
    </div>

    <!-- 引用 HiTeach 名單選項（需校園版授權 + 版本 >= 5.5.0.0002） -->
    <div v-if="isNewPushFuncVersion && schoolVers" class="setting-section roster-section">
      <div class="roster-checkbox-wrapper">
        <label class="checkbox-container">
          <input type="checkbox" v-model="useHiTeachRoster" class="roster-checkbox" />
          <span class="checkbox-label">{{ $t('HTwbActKitHome.useHiTeachRoster') }}</span>
        </label>
      </div>
    </div>

    <div class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('PvPgame.clearData') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('PvPgame.saveSettings') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, getCurrentInstance, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';
import { useSchoolVersion } from '@/composable/useSchoolVersion';
import { setupUserAuth } from '@/composable/useGameMessageBridge';

const router = useRouter();
const that = getCurrentInstance().proxy;

// HiTeach 版本管理
const { isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTpvpGameSetting');

// 校園版授權判斷
const { schoolVers } = useSchoolVersion();

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// 檢查是否在 iframe 環境中
const isInIframe = computed(() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // 如果拋出異常，說明在跨域 iframe 中
  }
});

onMounted(() => {
  messageBridge.addEventListener(event => {
    const msg = event.data;
    console.log(msg, 'msg');
    if (msg.action === 'Init.GameHome') {
      setupUserAuth(msg.data, 'HTpvpGameSetting');
    }
  });

  // 載入 HiTeach 版本號
  loadVersion();

  let rosterFromConfig = false;

  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('PvPGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      originalConfig.value = config; // 記錄原始設定
      console.log('從 sessionStorage 讀取到的配置:', config);

      // 填充表單欄位
      if (config.topic) topic.value = config.topic;
      if (config.correctVocabularyList && Array.isArray(config.correctVocabularyList)) {
        vocabularyList.value = [...config.correctVocabularyList];
      }
      if (config.errorVocabularyList && Array.isArray(config.errorVocabularyList)) {
        errorVocabularyList.value = [...config.errorVocabularyList];
      }
      if (config.gameDuration) gameDuration.value = config.gameDuration;
      if (config.background) background.value = config.background;
      if (config.gameSpeed) gameSpeed.value = config.gameSpeed;
      if (typeof config.useHiTeachRoster !== 'undefined') {
        useHiTeachRoster.value = !!config.useHiTeachRoster;
        rosterFromConfig = true;
      }

    } catch (error) {
      console.error('解析 sessionStorage 中的配置時出錯:', error);
    }
  }

  // 校園版且未從配置載入名單偏好 → 預設勾選
  if (!rosterFromConfig && schoolVers.value) {
    useHiTeachRoster.value = true;
  }
});

const $Message = inject("$Message");

const topic = ref('');

// HiTeach 名單選項
const useHiTeachRoster = ref(false);

// 正確詞彙列表
const vocabularyInput = ref('');
const vocabularyList = ref([]);

// 錯誤詞彙列表
const errorVocabularyInput = ref('');
const errorVocabularyList = ref([]);

// 遊戲時長選項
const durationOptions = [20, 30, 40];
const gameDuration = ref(30);

// 遊戲速度選項
const speedOptions = [3, 2, 1];
const gameSpeed = ref(2); // 預設中速
const originalConfig = ref(null); // 記錄載入時的原始設定


// 計算是否可以開始遊戲
const canStartGame = computed(() => vocabularyList.value.length > 0 && errorVocabularyList.value.length > 0);

// 根據速度值獲取對應的語系文字
const getSpeedText = (speed) => {
  if (speed === 3) return that.$t('PvPgame.speedFast');
  if (speed === 2) return that.$t('PvPgame.speedMedium');
  if (speed === 1) return that.$t('PvPgame.speedSlow');
  return speed;
};

// 處理正確詞彙的按鍵事件
const handleKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  // 如果按下Enter，則處理所有逗號分隔的詞彙
  if (event.key === 'Enter') {
    event.preventDefault();
    addVocabulary();
  }
  // 如果按下逗號，只在輸入框中添加逗號，不進行處理
  // 這樣用戶可以輸入多個詞彙，用逗號分隔，然後按Enter一次性添加
};

// 處理錯誤詞彙的按鍵事件
const handleErrorKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  // 如果按下Enter，則處理所有逗號分隔的詞彙
  if (event.key === 'Enter') {
    event.preventDefault();
    addErrorVocabulary();
  }
};

// 新增正確詞彙
const addVocabulary = () => {
  // 分割輸入的文本，使用中英文逗號和換行符號作為分隔符
  const words = vocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  // 添加所有有效的詞彙
  for (const word of words) {

    if (vocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('PvPgame.wordExists'),
        timeout: 2000,
      });
    }
    if (vocabularyList.value.length < 20 && !vocabularyList.value.includes(word.trim())) {
      vocabularyList.value.push(word.trim());
    }

  }
  if (vocabularyList.value.length == 20) {
    $Message.warning({
      content: that.$t('PvPgame.maxVocabulary'),
      timeout: 2000,
    });
  }
  // 清空輸入框
  vocabularyInput.value = '';
};

// 新增錯誤詞彙
const addErrorVocabulary = () => {
  // 分割輸入的文本，使用中英文逗號和換行符號作為分隔符
  const words = errorVocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  // 添加所有有效的詞彙
  for (const word of words) {
    if (errorVocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('PvPgame.wordExists'),
        timeout: 2000,
      });
    }
    if (errorVocabularyList.value.length < 20 && !errorVocabularyList.value.includes(word.trim())) {
      errorVocabularyList.value.push(word.trim());
    }

  }
  if (errorVocabularyList.value.length == 20) {
    $Message.warning({
      content: that.$t('PvPgame.maxErrorVocabulary'),
      timeout: 2000,
    });
  }
  // 清空輸入框
  errorVocabularyInput.value = '';
};

// 移除正確詞彙
const removeVocabulary = (index) => {
  vocabularyList.value.splice(index, 1);
};

// 移除錯誤詞彙
const removeErrorVocabulary = (index) => {
  errorVocabularyList.value.splice(index, 1);
};

// 清空設定數據
const clearData = () => {
  topic.value = '';
  vocabularyList.value = [];
  vocabularyInput.value = '';
  errorVocabularyList.value = [];
  errorVocabularyInput.value = '';
  gameDuration.value = 30;
  gameSpeed.value = 2; // 預設中速
  background.value = 'ocean';
  
  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('PvPGameConfig');
  
};

// 開始遊戲
const startGame = () => {
  // 檢查正確詞彙輸入框是否有未按 Enter 的內容
  const hasUnsubmittedCorrect = vocabularyInput.value.trim() !== '';
  // 檢查錯誤詞彙輸入框是否有未按 Enter 的內容
  const hasUnsubmittedError = errorVocabularyInput.value.trim() !== '';

  if (hasUnsubmittedCorrect || hasUnsubmittedError) {
    // 為有內容的輸入框添加紅框
    const correctTextarea = document.querySelector('.correct-section textarea');
    const errorTextarea = document.querySelector('.error-section textarea');

    if (hasUnsubmittedCorrect && correctTextarea) {
      correctTextarea.style.border = '2px solid red';
    }
    if (hasUnsubmittedError && errorTextarea) {
      errorTextarea.style.border = '2px solid red';
    }

    // 顯示提示訊息
    $Message.warning({
      content: that.$t('PvPgame.unsubmittedWarning'),
      timeout: 3000,
    });

    return; // 不執行儲存動作
  }

  const gameSettings = {
    topic: topic.value,
    correctVocabularyList: JSON.parse(JSON.stringify(vocabularyList.value)),
    errorVocabularyList: JSON.parse(JSON.stringify(errorVocabularyList.value)),
    gameDuration: gameDuration.value,
    background: background.value,
    gameSpeed: gameSpeed.value,
    useHiTeachRoster: useHiTeachRoster.value
  };

  console.log('遊戲設定:', gameSettings);

  // 比對設定是否有變動（僅在有過去推送時才需比對）
  if (originalConfig.value?.restoredData) {
    const orig = originalConfig.value;
    const origComp = JSON.stringify({ topic: orig.topic, correctVocabularyList: orig.correctVocabularyList, errorVocabularyList: orig.errorVocabularyList, gameDuration: orig.gameDuration, background: orig.background, gameSpeed: orig.gameSpeed, useHiTeachRoster: orig.useHiTeachRoster });
    const currComp = JSON.stringify({ topic: gameSettings.topic, correctVocabularyList: gameSettings.correctVocabularyList, errorVocabularyList: gameSettings.errorVocabularyList, gameDuration: gameSettings.gameDuration, background: gameSettings.background, gameSpeed: gameSettings.gameSpeed, useHiTeachRoster: gameSettings.useHiTeachRoster });
    if (origComp === currComp) {
      $Message.warning({ content: that.$t('Game.settingsUnchanged'), timeout: 3000 });
      gameSettings.restoredData = orig.restoredData;
    }
  }

  // 🔧 改善：只有在 iframe 環境中才優先使用 sessionStorage
  if (isInIframe.value) {
    console.log('[iframe 模式] 優先將設定存入 sessionStorage');
    try {
      sessionStorage.setItem('PvPGameConfig', JSON.stringify(gameSettings));
      console.log('✅ [iframe] 遊戲設定已儲存至 sessionStorage');
    } catch (error) {
      console.error('❌ [iframe] 儲存設定至 sessionStorage 失敗:', error);
      $Message.error({
        content: that.$t('PvPgame.saveFailed') || '儲存失敗，請重試',
        timeout: 3000,
      });
      return;
    }

    // iframe 環境：發送 Init.PvPGame 訊息
    try {
      messageBridge.postMessage({
        action: 'Init.PvPGame',
        data: gameSettings
      });
      console.log('📤 [iframe] 已發送 Init.PvPGame 訊息');
    } catch (error) {
      console.warn('⚠️ [iframe] 發送訊息失敗，但設定已存入 sessionStorage:', error);
    }
  } else {
    // 非 iframe 環境：維持原有邏輯，發送 Save.PvPGameSetting 訊息
    console.log('[非 iframe 模式] 使用原有訊息機制');
    messageBridge.postMessage({
      action: 'Save.PvPGameSetting',
      data: gameSettings
    });
    console.log('📤 [非 iframe] 已發送 Save.PvPGameSetting 訊息');
  }

  // 跳轉到遊戲頁面
  router.push('/ht-pvpgame-play');
};


// 背景選項資料
const backgroundData = [
  { value: 'ocean', image: require('@/assets/HTwbActAssets/ocean_world.webp') },
  { value: 'desert', image: require('@/assets/HTwbActAssets/desert_oasis.webp') },
  { value: 'forest', image: require('@/assets/HTwbActAssets/fantasy_forest.webp') }
];

const background = ref('ocean');


</script>

<style lang="less" scoped>
.game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px 0px 20px 0px;
}

.top-action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1rem;
    cursor: pointer;
    font-family: inherit;
    &:hover { background-color: #da190b; }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1rem;
    cursor: pointer;
    font-family: inherit;
    &:hover { background-color: #45a049; }
    &:disabled { background-color: #cccccc; cursor: not-allowed; }
  }
}

.game-title {
    font-size: 2rem;
    text-align: center;
  }

.setting-section {
  position: relative;
  margin-bottom: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;

  .setting-title {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
  .input-text{
    width: 100%;
    height: 40px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    margin-bottom: 10px;
  }
  &.correct-section{
    background-color: #e8f4ff;
    padding: 20px;
    .setting-title {
      color: blue;
    }
  }

  &.error-section {
    background-color: #fff0f3;

    .setting-title {
      color: #d81b60;
    }
  }
}

.vocabulary-input {
  textarea {
    width: 100%;
    height: 100px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    margin-bottom: 10px;
  }
}

.vocabulary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;;
  margin-bottom: 10px;

  .vocabulary-tag {
    background-color: #e8f4ff;
    border: 1px solid #b8dcff;
    border-radius: 20px;
    padding: 5px 10px;
    display: flex;
    align-items: center;
    font-size: 0.9rem;

    .delete-tag {
      margin-left: 5px;
      cursor: pointer;
      font-weight: bold;
      color: #666;

      &:hover {
        color: #ff4d4d;
      }
    }

    &.error-tag {
      background-color: #ffe0e9;
      border: 1px solid #ffb6c9;

      .delete-tag {
        &:hover {
          color: #d81b60;
        }
      }
    }
  }
}

.vocabulary-counter {
  position: absolute;
  right: 22px;
  top: 25px;
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
}

.duration-selector {
  select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 1rem;
  }
}

.background-selector {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 10px;
  
  .background-option {
    width: 150px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    border: 3px solid transparent;
    
    img {
      width: 100%;
      height: 100px;
      object-fit: cover;
      display: block;
    }
    
    .background-label {
      padding: 8px;
      text-align: center;
      background-color: #f5f5f5;
      font-size: 0.9rem;
    }
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }
    
    &.selected {
      border-color: #4caf50;
      
      &::after {
        content: '✓';
        position: absolute;
        top: 5px;
        right: 5px;
        width: 25px;
        height: 25px;
        background-color: #4caf50;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
      }
      
      .background-label {
        background-color: #e8f5e9;
        color: #2e7d32;
        font-weight: bold;
      }
    }
  }
}

// 名單選項區塊
.roster-section {
  .roster-checkbox-wrapper {
    display: flex;
    align-items: center;
  }

  .checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .roster-checkbox {
      width: 20px;
      height: 20px;
      cursor: pointer;
      margin-right: 10px;
    }

    .checkbox-label {
      font-size: 1.2rem;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #da190b;
    }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}
</style>